import type { TaskPrReview } from '../factory-review';
import type { TaskPrNoPictures } from '../pr-pictures';
/**
 * Tasks: a project's work items on its Board, optionally tied to pages and web pages (Contexts).
 * Design: docs/task-system-design.md
 */

import type { TaskRepeat } from '../task-repeat';
import type { LiveChangeMessage } from './live';
import type { TaskSourceKind, TaskSourceRefs, TaskUpstream } from './task-sources';
import type { ProjectRole, ProjectSummary } from './factory';

/**
 * What a task can be about. `page` is any page in the app (path + query); `url` is a web page
 * outside it. Entity Contexts (projects, runs, PRs) come with those features.
 */
export const TASK_CONTEXT_TYPES = ['page', 'url'] as const;

export type TaskContextType = (typeof TASK_CONTEXT_TYPES)[number];

export function isTaskContextType(value: unknown): value is TaskContextType {
  return typeof value === 'string' && (TASK_CONTEXT_TYPES as readonly string[]).includes(value);
}

/** A reference to a Context: `id` is a uuid, except for `page` (path + query) and `url` (the full address). */
export interface TaskContextRef {
  type: TaskContextType;
  id: string;
}

export type TaskContextSource = 'page' | 'mention' | 'manual';

export interface TaskContext extends TaskContextRef {
  title: string;
  /** Where "open" goes. */
  url: string;
  /** Parent Context, so a page also lists tasks on its children (none yet for page and url). */
  parent: TaskContextRef | null;
  source: TaskContextSource;
}

export interface TaskColumn {
  id: string;
  name: string;
  position: number;
  is_done: boolean;
  /** SF-T-218: a person moving a task here asks the AI builder (Board settings › Columns); a new project's Ready column. */
  asks_ai: boolean;
}

export interface TaskLabel {
  id: string;
  name: string;
  color: TaskLabelColor;
  position: number;
}

export const TASK_LABEL_COLORS = [
  'gray',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const;

export type TaskLabelColor = (typeof TASK_LABEL_COLORS)[number];

/** The bespoke fields a task type can turn on, in the order they show. */
export const TASK_TYPE_FIELDS = ['size', 'kind', 'priority', 'prs'] as const;
export type TaskTypeField = (typeof TASK_TYPE_FIELDS)[number];

export const TASK_SIZES = ['XS', 'S', 'M', 'L', 'XL'] as const;
export type TaskSize = (typeof TASK_SIZES)[number];

export const TASK_KINDS = ['fix', 'performance', 'feature', 'chore', 'docs', 'refactor', 'ci'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

/**
 * SF-T-151: how urgent a task is, most urgent first (no priority is below Low). A mirrored task's comes
 * from its source when the source maps one (`priority_map`), and is read-only here then.
 */
export const TASK_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };

export function isTaskPriority(value: unknown): value is TaskPriority {
  return typeof value === 'string' && (TASK_PRIORITIES as readonly string[]).includes(value);
}

/** 0 for Urgent up to 3 for Low, and 4 for none: the AI builder's pick order and the Board's sort. */
export function taskPriorityRank(priority: TaskPriority | null | undefined): number {
  return priority ? TASK_PRIORITIES.indexOf(priority) : TASK_PRIORITIES.length;
}

export const TASK_PR_STATUSES = ['draft', 'open', 'merged', 'closed'] as const;
export type TaskPrStatus = (typeof TASK_PR_STATUSES)[number];

/** A pull request on a task, set by a person or the task loop (the worker never reads GitHub). */
export interface TaskPr {
  url: string;
  status: TaskPrStatus;
  /** T-36: the second-model review's outcome, written by the factory only (a person's edit keeps it, never sets it). */
  review?: TaskPrReview;
  /** SF-T-79: the factory found it conflicting with its base, and what it did about it. Like `review`, only the factory writes it. */
  conflict?: TaskPrConflict;
  /** SF-T-164: open, it changes files people see and its description has no pictures. Only the factory writes it. */
  no_pictures?: TaskPrNoPictures;
}

/**
 * SF-T-79: a pull request the factory found conflicting with its base branch. One pair of head and base
 * commits is acted on once; `tries` counts the AI builder's tries at that base commit.
 */
export interface TaskPrConflict {
  /**
   * SF-T-183: why it was handed over: `conflicts` (absent on older notes), `migrations` (main has newer
   * migrations: Update on the card), `db_check` (a database check failed on its head).
   */
  kind?: TaskPrHandOffKind;
  head_sha: string;
  base_sha: string;
  /** The PR's base branch, and its head branch (what the fix pushes to). */
  base_ref: string;
  head_ref: string;
  /** When the factory saw it conflicting. */
  at: string;
  /** Hand-offs to the AI builder at this base commit (at most CONFLICT_MAX_TRIES). */
  tries: number;
  /** When it was last handed to the AI builder (null: it was handed to a person, or left). */
  handed_off_at: string | null;
  /** The run that took the hand-off, once one started (a later ask is an ordinary one). */
  run_number: number | null;
  /** The column the task was in, which the fix puts it back in. */
  column: string | null;
}

/** SF-T-183: what a PR hand-off to the AI builder fixes (the fix-conflicts flow, each with its own steps). */
export const TASK_PR_HAND_OFF_KINDS = ['conflicts', 'migrations', 'db_check'] as const;
export type TaskPrHandOffKind = (typeof TASK_PR_HAND_OFF_KINDS)[number];

/** SF-T-79: the AI builder's automatic tries at fixing one PR's conflicts with one base commit. */
export const CONFLICT_MAX_TRIES = 2;

/** SF-T-79: the flow that fixes a PR's conflicts. Only a conflict hand-off starts it (not the Run dialog): it needs the PR. */
export const CONFLICT_FLOW_ID = 'fix-conflicts';

/** A task type. Every task has one; the default (Task) can't be deleted. */
export interface TaskType {
  id: string;
  name: string;
  color: TaskLabelColor;
  position: number;
  is_default: boolean;
  /** The bespoke fields this type shows, in TASK_TYPE_FIELDS order. */
  fields: TaskTypeField[];
}

/** The type every project starts with: development work, so size, kind, priority and PRs show. */
export const DEFAULT_TASK_TYPES: Array<Pick<TaskType, 'name' | 'color' | 'is_default' | 'fields'>> = [
  { name: 'Task', color: 'gray', is_default: true, fields: ['size', 'kind', 'priority', 'prs'] },
];

/**
 * Waiting on: whose turn it is. A person, another task (until it's done), the AI builder (the
 * factory), or upstream (T-58: the task's source, while questions or suggestions sent there wait for a
 * reply; only on a task mirrored from an enabled source).
 */
export type TaskWaitingRef = { target: 'user'; user_id: string } | { target: 'task'; task_id: string } | { target: 'ai' } | { target: 'upstream' };

/** As a card carries it: a waited-on task with its number, title and whether it's done; upstream with the source's name ("Asana"). */
export type TaskWaiting =
  | { target: 'user'; user_id: string }
  | { target: 'task'; task_id: string; number: number; title: string; done: boolean }
  | { target: 'ai'; pickup_skip?: TaskPickupSkip }
  | { target: 'upstream'; name: string };

/** What an upstream wait is called when the task's source isn't known any more. */
export const TASK_UPSTREAM_WAIT_NAME = 'Upstream';

/**
 * T-65: why the AI builder hasn't picked the task up yet, from its latest try: `short` for the card
 * ("Machines busy"), `reason` in full for the task panel, `since` when that reason was first given.
 * Gone once a run starts.
 */
export interface TaskPickupSkip {
  short: string;
  reason: string;
  since: string;
}

/** The AI builder's name wherever it shows. */
export const TASK_AI_BUILDER_NAME = 'AI builder';

export interface TaskMember {
  user_id: string;
  displayname: string | null;
  email: string;
  photourl: string | null;
  /** Their @mention name (users.handle), e.g. "andrew"; shown where room is tight. */
  handle?: string | null;
  /** Their effective role on the project (SF-T-215: the screen trusts a member's comments to the AI builder). */
  role?: ProjectRole;
}

/** A task as it appears on the Board and in Tasks panels. */
/**
 * T-14: the task a stacked task builds on. Its runs start from that task's open PR branch; once that
 * task's PR is merged (`pr_merged`), the stacked task's PR should go against the default branch.
 */
/** A task found by its number (GET /api/tasks/refs, T-14): enough to link to it. */
export interface TaskRef {
  id: string;
  number: number;
  title: string;
  done: boolean;
}

/** GET /api/tasks/parents (SF-T-172): a task another can be made a sub-task of, for Make sub-task's picker. */
export type TaskParentChoice = Pick<TaskRef, 'id' | 'number' | 'title'>;

export interface TaskStackedOn {
  task_id: string;
  number: number;
  title: string;
  pr_merged: boolean;
}

/** T-42: the task a sub-task belongs to. */
export interface TaskParent {
  task_id: string;
  number: number;
  title: string;
  /**
   * T-71: a synced sub-task whose upstream task is no longer under this parent there (made a task of its
   * own, or moved under one that isn't here). It's left here, marked.
   */
  orphaned?: boolean;
  /**
   * SF-T-154: the parent's pull requests (their URLs). One the sub-task lists too is shared by the parent's
   * sub-tasks and merged from the parent (sharedPrParent).
   */
  pr_urls?: string[];
  /** SF-T-207: the source the parent is mirrored from, when it is: a sub-task made here can be pushed under it there. */
  source_id?: string | null;
}

/** SF-T-154: the parent a sub-task's PR is shared with (its home, where it's merged), or null for a PR of its own. */
export function sharedPrParent(task: { parent: TaskParent | null }, url: string): TaskParent | null {
  return task.parent?.pr_urls?.includes(url) ? task.parent : null;
}

/** T-42: how many of a task's sub-tasks are done (in a done column), of those not archived by hand. */
export interface TaskSubtaskCount {
  done: number;
  total: number;
}

/** T-92: a person its open sub-tasks wait on, with those sub-tasks (oldest number first). */
export interface TaskSubtaskWaiting {
  user_id: string;
  tasks: Array<{ task_id: string; number: number }>;
}

/** SF-T-176: a sub-task as its parent's card carries it, for the Board's search. */
export interface TaskSubtaskTitle {
  task_id: string;
  number: number;
  title: string;
}

/** SF-T-174: the machine chosen for the AI builder's runs of a task. */
export interface TaskAiMachine {
  id: string;
  name: string;
}

/** SF-T-267: the most sub-tasks a one-at-a-time parent has with the AI builder at once. */
export const SUBTASK_CONCURRENCY_MAX = 10;

/** SF-T-267: a sub-task another waits on. */
export interface TaskDependencyRef {
  task_id: string;
  number: number;
}

export interface TaskCard {
  id: string;
  /** Per-project task number, shown as T-42 (formatTaskNumber). */
  number: number;
  title: string;
  column_id: string;
  rank: string;
  assignee_ids: string[];
  label_ids: string[];
  /** Its type: never null, the project's default when none was picked. */
  type_id: string;
  /** Bespoke fields; shown when the type turns them on, kept when it doesn't. */
  size: TaskSize | null;
  kind: TaskKind | null;
  /** SF-T-151: how urgent it is; null for none. On a mirrored task whose source maps priority, upstream's. */
  priority: TaskPriority | null;
  /**
   * The fields Jev filled in (T-17): type, kind and size each stay marked as suggested until a person
   * confirms or changes them. Jev only fills empty or still-suggested fields.
   */
  ai_suggested: TaskSuggestedField[];
  /** Its AI summary (T-17), null until the first one is made. */
  ai_summary: TaskAiSummary | null;
  /** When the next summary is due (ISO), or null. */
  ai_summary_due_at: string | null;
  prs: TaskPr[];
  /** T-14: the task this one builds on (a suggestion taken up on a new branch), or null. */
  stacked_on: TaskStackedOn | null;
  /**
   * SF-T-174: the fleet machine the AI builder runs it on, chosen by a person; null lets the pick-up
   * choose. It stays on the task until someone changes it.
   */
  ai_machine: TaskAiMachine | null;
  /** T-42: the task this is a sub-task of, or null. */
  parent: TaskParent | null;
  /** T-42: a sub-task that's on the Board too (sub-tasks are off it otherwise). Always false for other tasks. */
  show_on_board: boolean;
  /**
   * SF-T-242: the AI builder builds this task's sub-tasks one at a time, each in its own run with its own PR stacked
   * on the one before, rather than all in one batch. Always false for sub-tasks.
   */
  subtasks_one_at_a_time: boolean;
  /** SF-T-242: the AI builder's runs of its sub-tasks (and of it) build without stopping to ask, listing their questions for later. */
  subtasks_defer_questions: boolean;
  /** SF-T-263: a sub-task picked for its one-at-a-time parent's chain, until the picked set is built. */
  in_chain: boolean;
  /** SF-T-269: a sub-task where its one-at-a-time parent's chain stops for a person, once it has its PR. */
  chain_break: boolean;
  /** SF-T-269: when the chain went past this break (Continue): it no longer stops the chain, and shows as passed. */
  chain_break_passed_at: string | null;
  /**
   * SF-T-269: on a one-at-a-time parent, the person its chain was handed back to when it stopped at a break, until it's
   * handed to the AI builder again (Continue); null when it isn't stopped.
   */
  subtasks_chain_stopped_for: string | null;
  /** SF-T-267: how many of its sub-tasks the one-at-a-time pick-up has with the AI builder at once (1 by default). */
  subtasks_concurrency: number;
  /** SF-T-267: on a sub-task, the sub-tasks of the same parent it waits on, in the order they were added. */
  after: TaskDependencyRef[];
  /**
   * SF-T-279: in a task detail's subtask_cards only, a run going on it (its own, or a batch that lists it): the sub-task
   * list shows what waits on it as unlocked only once it has its PR and this is false.
   */
  run_going?: boolean;
  /**
   * T-27: made by the AI builder (asked to, in a run) and not confirmed by a person yet. The AI builder
   * never picks a draft up; a person promotes it (or archives it). Asking the AI builder to build it, or
   * pressing Run, promotes it too, when a person does it.
   */
  draft: boolean;
  /** T-42: its sub-tasks' progress (the card's x/y), or null when it has none. */
  subtasks: TaskSubtaskCount | null;
  /**
   * T-92: the people its open sub-tasks (not done, not archived by hand) wait on, so its card can say
   * it's your turn. Waits on tasks or the AI builder aren't rolled up. Empty for sub-tasks.
   */
  subtask_waiting: TaskSubtaskWaiting[];
  /**
   * SF-T-176: its sub-tasks (not archived by hand), number order, so the Board's search finds the parent
   * by a sub-task's number or title. Empty for sub-tasks.
   */
  subtask_titles: TaskSubtaskTitle[];
  /** Whose turn it is, oldest first. */
  waiting_on: TaskWaiting[];
  /** The first image or video in its Attachments, or null. */
  cover_attachment_id: string | null;
  /** Who follows it (muted followers left out): the Board's Following view. */
  follower_ids: string[];
  /** `#tags` from the title, description and chat (lower case, no #). */
  tags: string[];
  contexts: TaskContext[];
  chat_count: number;
  attachment_count: number;
  /** T-57: the coding agent's patch requests still waiting for a Maintainer (shared/patch-request.ts). */
  pending_patches: number;
  /** SF-T-177: permission requests the run's coding agent waits on in T3 Code (shared/approval-request.ts). */
  pending_approvals: number;
  /** SF-T-206: its newest run, when that failed (the card's "Run failed" tag): its number and the error's first line. */
  failed_run: TaskFailedRun | null;
  created_by_user_id: string | null;
  /**
   * SF-T-176: who it's for: its maker, else whoever first sent it to the AI builder (worker
   * services/tasks/requestor.ts). Shown top left of its card and task; null when neither is known.
   */
  requested_by_user_id: string | null;
  /** Archived by hand, or auto-archived 14 days after reaching a done Column. */
  archived: boolean;
  done_at: string | null;
  /** Snoozed until then: off the Board and out of counts. A past time means it's back. */
  snoozed_until: string | null;
  /** Repeat: it comes back to the top of the first Column on each day this lands on. */
  repeat: TaskRepeat | null;
  /** When the Repeat next brings it back; null without one. */
  repeat_next_at: string | null;
  /**
   * T-15: where it's mirrored from (shared/types/task-sources.ts), or null for the factory's own
   * tasks. Its title and description are then read-only here.
   */
  upstream: TaskUpstream | null;
  created_at: string;
  updated_at: string;
}

/** SF-T-206: a task's newest run that failed. */
export interface TaskFailedRun {
  number: number;
  error: string | null;
}

/** The fields Jev can suggest on a task (T-17). */
export const TASK_SUGGESTED_FIELDS = ['type', 'kind', 'size'] as const;
export type TaskSuggestedField = (typeof TASK_SUGGESTED_FIELDS)[number];

/** A task's AI summary (T-17): two sentences for someone who hasn't followed it. */
export interface TaskAiSummary {
  /** What the task is and where it belongs, in a tense that follows its state. */
  what: string;
  /** Where it stands and who it's waiting for. */
  status: string;
  /** When it was made (ISO). */
  made_at: string;
  /** The Column it was made for. */
  state: string;
  model: string;
  /** The prompt file's commit (factory/ai-prompts/task-summary.md), 'dev' for a local build. */
  prompt_commit: string;
  log_id: string | null;
  /** The task's activity count when it was made (task.ai_summary_seq): a later request with a lower count never replaces it. */
  activity_seq: number;
}

export type TaskEventKind =
  | 'created'
  | 'chat'
  | 'moved'
  | 'renamed'
  | 'described'
  /** T-59: its spec changed: payload `{ before }` (the old spec, fetched on its own like a description's). */
  | 'spec_changed'
  | 'assigned'
  | 'unassigned'
  | 'context_added'
  | 'context_removed'
  | 'label_added'
  | 'label_removed'
  | 'archived'
  | 'unarchived'
  /** SF-T-198: one of its pull requests was merged by a Merge PRs batch: payload `{ url, position, total }`. */
  | 'pr_batch_merged'
  /** T-27: a person promoted the AI builder's draft: payload `{ via: 'promote' | 'ai_builder' | 'run' }`. */
  | 'promoted'
  | 'snoozed'
  | 'unsnoozed'
  /** Someone set, changed or removed its Repeat: payload `{ repeat: TaskRepeat | null }`. */
  | 'repeat_set'
  /** Its Repeat brought it back: payload `{ reopened, from, to, next_at }` (no actor). */
  | 'repeated'
  /** Its type changed: payload `{ from, to, from_name, to_name }`. */
  | 'type_set'
  /** A bespoke field changed: payload `{ field, from, to }` (prs: the lists). */
  | 'field_set'
  /** Someone is waited on now: payload `{ waiting: TaskWaitingRef, name }`. */
  | 'waiting_added'
  /**
   * A wait ended: payload `{ waiting, name, reason }`, reason `replied` (they commented), `done` (the
   * waited-on task finished), `review` (moved to Review, for the AI builder), `removed` (by hand,
   * or replaced), and for upstream (T-58) `upstream_replied` (a reply there was matched), `answered`
   * (the round was answered here) or `unmirrored` (the task isn't mirrored from an enabled source now).
   */
  | 'waiting_removed'
  /**
   * T-71: its source board took this sub-task off its parent (left under it here, marked orphaned), or
   * put it back (`back`): payload `{ back, parent: { id, number, title } | null }`, by the source's label.
   */
  | 'orphaned'
  /**
   * SF-T-147: a sub-task became a task of its own. On the task: payload `{ parent: { id, number, title } }`;
   * on its old parent: `{ subtask: { id, number, title } }`.
   */
  | 'subtask_promoted'
  /**
   * SF-T-132: a task was put under a parent (back from being promoted, or part of a larger task). On the task:
   * payload `{ parent: { id, number, title } }`; on the parent: `{ subtask: { id, number, title } }`.
   */
  | 'made_subtask'
  /** SF-T-146: someone deleted a file from the task: payload `{ filename }`. */
  | 'attachment_deleted'
  /**
   * SF-T-103: tasks merged into one on the Board. On each task put under the parent: payload
   * `{ parent: { id, number, title } }`. On the parent: `{ subtasks: [{ id, number, title }], created }`.
   */
  | 'merged'
  /** SF-T-207: someone pushed this task, made here, to a source: payload `{ source_id, source_name, kind, ref, url }`. Mirrored from it since. */
  | 'pushed_upstream'
  /**
   * SF-T-279: someone closed runs that worked on this task (a run's whole T3 thread): payload `{ runs }` (their numbers).
   * Their open questions close unanswered (questionRounds), and they're never carried on.
   */
  | 'runs_closed';

export interface TaskAttachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  /** Signed URL, valid for about an hour. */
  url: string;
  is_image: boolean;
  created_at: string;
  /** A square from its top-left corner, for images; null for other files. Signed like `url`. */
  square_url?: string | null;
}

export interface TaskEvent {
  id: string;
  kind: TaskEventKind;
  actor_user_id: string | null;
  /** A non-person actor, e.g. "Factory · R-12" (a run's step); actor_user_id is then null. */
  actor_label: string | null;
  /** Set when the actor's API key made this: shown as "Andrew (via Claude key)". */
  via_api_key_name: string | null;
  /** Chat text (with Mention markup), or null for system lines. */
  body: string | null;
  /**
   * T-70: a long comment's one-line AI version (shared/comment-short.ts), shown with More for the whole
   * comment; null until it's made, and again once the comment is edited.
   */
  ai_short?: string | null;
  /** Kind-specific details, e.g. { from_column, to_column } or { context }. */
  payload: Record<string, unknown>;
  attachments: TaskAttachment[];
  created_at: string;
  edited_at: string | null;
}

/** SF-T-271: one sub-task as the AI's suggested order read it: what it named, and whether it was a break. */
export interface SubtaskOrderEntry {
  task_id: string;
  /** The sub-tasks of the same parent it names (ids), as they were. */
  after: string[];
  chain_break: boolean;
}

/** SF-T-271: one sub-task the AI suggests changing: what it waits on and whether it's a break, then and suggested. */
export interface SubtaskOrderChange {
  task_id: string;
  number: number;
  was_after: TaskDependencyRef[];
  was_break: boolean;
  /** The sub-tasks it would name (none: it waits on the one before it in the list, as one that names none does). */
  after: TaskDependencyRef[];
  chain_break: boolean;
  /** Why, in a sentence. */
  why: string;
}

/**
 * SF-T-271: the AI's suggested dependencies and breaks for a parent's sub-tasks (Suggest the order), kept on the parent
 * until a person accepts them all in one go or dismisses them. Nothing is applied until then.
 */
export interface SubtaskOrderSuggestion {
  id: string;
  /** Its sub-tasks (not drafts) in the list's order, as the AI read them: once they differ, it's out of date. */
  graph: SubtaskOrderEntry[];
  /** Only the sub-tasks it would change; none when it found the order right as it is. */
  changes: SubtaskOrderChange[];
  /**
   * The list order it suggests (every sub-task in `graph`), or null/absent to keep the list as it is. One that names
   * none waits on the one before it in this order.
   */
  order?: TaskDependencyRef[] | null;
  /** A sentence or two on the order it suggests. */
  summary: string;
  /** When it was asked (the AI's call started): of two asked at once, the later asked is kept. Absent on older ones. */
  asked_at?: string;
  made_at: string;
  /** Who asked (a user id). */
  made_by: string | null;
  model: string | null;
  /** Where it was asked: the sub-task list, a chat, or an agent's `sf-agent tasks graph suggest` (posted on the task). */
  source: 'task' | 'chat' | 'agent';
}

/** SF-T-271: `sf-agent tasks graph suggest`'s answer (the run's and the chat's). */
export interface SubtaskOrderSuggestResult {
  /** The parent's ref and link. */
  task: { ref: string; url: string };
  suggestion: SubtaskOrderSuggestion;
}

export interface TaskDetail extends TaskCard {
  description: string;
  /**
   * The description's version (ISO): an edit sends it back as `expected_updated_at`, and a
   * description someone else saved since is refused (409 TASK_DESCRIPTION_STALE).
   */
  description_updated_at: string;
  /**
   * T-59: its spec, Markdown beside the description: written by feedback runs and edited by people, on a
   * mirrored task too (its title and description stay its source's). '' when there's none.
   */
  spec: string;
  /** The spec's version (ISO), sent back as `expected_spec_updated_at` (409 TASK_SPEC_STALE). */
  spec_updated_at: string;
  /** True when the signed-in user follows this task and hasn't muted it. */
  following: boolean;
  events: TaskEvent[];
  /** In Attachments order: the first image or video is the cover. */
  attachments: TaskAttachment[];
  /** T-42: its sub-tasks (archived by hand left out), oldest first. Empty for a sub-task. */
  subtask_cards: TaskCard[];
  /**
   * SF-T-271: on a parent, the AI's suggested order for its sub-tasks (Suggest the order), until someone accepts or
   * dismisses it; null when there's none.
   */
  subtasks_suggestion?: SubtaskOrderSuggestion | null;
  /**
   * SF-T-76: on a mirrored task, the people and tasks its description and synced-in comments name on its
   * source, by name and title. Absent or null on the factory's own tasks.
   */
  source_refs?: TaskSourceRefs | null;
}

export interface TaskBoard {
  columns: TaskColumn[];
  labels: TaskLabel[];
  /** The project's task types, in order. */
  types: TaskType[];
  members: TaskMember[];
  tasks: TaskCard[];
  /** Every tag in use on the project's tasks (archived ones too), most used first: the `#` picker and Tags filter. */
  tags: TaskTagCount[];
  /**
   * The Board loads only the latest finished tasks (TASK_BOARD_DONE_LIMIT, more after
   * Show older): how many more there are. Absent when every finished task is loaded (a Tasks panel's
   * board, the API).
   */
  older_done?: TaskOlderDone;
  /** SF-T-207: the project's sources that are turned on, which a task made here can be pushed to (oldest first). */
  push_sources?: TaskPushSource[];
}

/** SF-T-207: a source a task can be pushed to: Push to source… lists these. */
export interface TaskPushSource {
  id: string;
  kind: TaskSourceKind;
  name: string;
}

/**
 * GET /api/tasks/meta (SF-T-172): the Board without its tasks, for what only needs the project's
 * columns, labels, types, people and `#tags` (the `#` picker, the Inbox's task pop-up, New task).
 */
export type TaskBoardMeta = Pick<TaskBoard, 'columns' | 'labels' | 'types' | 'members' | 'tags' | 'push_sources'>;

/** GET /api/tasks-all/board (the master board, T-26): one visible project and its Board. */
export interface TaskBoardOfProject {
  project: ProjectSummary;
  /** Null: the person leaves this project off their board, and `show=` didn't ask for it. */
  board: TaskBoard | null;
}

/** GET /api/tasks-all/board: every visible project, and the ones the person leaves off. */
export interface TaskBoardsOfUser {
  projects: TaskBoardOfProject[];
  /** Projects left off this person's master board (PUT /api/tasks-all/hidden). */
  hidden_project_ids: string[];
}

/** Most project ids `show=` takes, and the most a person can leave off their board. */
export const BOARD_PROJECTS_MAX = 500;

/** GET /api/tasks-all/finished: one project's older finished tasks the search found. */
export interface TaskFinishedOfProject extends TaskFinishedPage {
  project_id: string;
}

/** Done tasks (archived ones too, in the Archived view) the Board hasn't loaded. */
export interface TaskOlderDone {
  remaining: number;
}

/** Older finished tasks a Board search finds, newest finished first. */
export interface TaskFinishedPage {
  tasks: TaskCard[];
  /** More matched than were sent: the search shows only the latest. */
  more: boolean;
}

/** The Board's search sends at most this many older finished tasks. */
export const TASK_FINISHED_SEARCH_LIMIT = 50;

/** Most finished tasks the Board loads at once; Show older stops there. */
export const TASK_BOARD_DONE_MAX = 1000;

/** How many finished tasks the Board loads, newest first; Show older loads as many again. */
export const TASK_BOARD_DONE_LIMIT = 30;

export interface TaskTagCount {
  tag: string;
  count: number;
}

/** Board filters, also stored in Saved views as a query string. */
export interface TaskFilters {
  assignee?: string[];
  label?: string[];
  context_type?: TaskContextType[];
  q?: string;
  include_archived?: boolean;
}

/** SF-T-103: most tasks picked for one merge (their own sub-tasks come along on top). */
export const TASK_MERGE_MAX = 50;

/**
 * SF-T-103: merge picked tasks into one, as its sub-tasks. `task_ids` in Board order. The parent is
 * `parent_id`, one of them, or else a new task made from `title` and `description`.
 */
export interface TaskMergeInput {
  task_ids: string[];
  parent_id?: string | null;
  title?: string;
  description?: string;
}

export interface TaskMergeResult {
  parent_id: string;
  /** The tasks put under it: the picked ones and their own sub-tasks. */
  moved: number;
}

export interface TaskCreateInput {
  /** Optional: chosen by the browser so the task can be shown and opened before the server answers. */
  id?: string;
  title: string;
  description?: string;
  column_id?: string;
  assignee_ids?: string[];
  label_ids?: string[];
  contexts?: Array<TaskContextRef & { title?: string }>;
  /** Left out, the project's default type. */
  type_id?: string;
  size?: TaskSize | null;
  kind?: TaskKind | null;
  priority?: TaskPriority | null;
  waiting_on?: TaskWaitingRef[];
  /** SF-T-174: the fleet machine the AI builder runs it on; null or left out lets the pick-up choose. */
  ai_machine_id?: string | null;
  /** T-42: make it a sub-task of this task (which can't be a sub-task itself). */
  parent_task_id?: string;
}

export interface TaskUpdateInput {
  title?: string;
  description?: string;
  /**
   * With `description`: the description's version the edit started from (TaskDetail's
   * `description_updated_at`). A description changed since is refused with 409
   * TASK_DESCRIPTION_STALE and nothing is saved. Left out, nothing is checked.
   */
  expected_updated_at?: string | null;
  /** T-59: the task's spec (Markdown, TASK_SPEC_MAX); '' clears it. Allowed on a mirrored task. */
  spec?: string;
  /**
   * With `spec`: the spec's version the edit started from (TaskDetail's `spec_updated_at`). A spec
   * changed since is refused with 409 TASK_SPEC_STALE. Left out (a run's spec), it's replaced.
   */
  expected_spec_updated_at?: string | null;
  assignee_ids?: string[];
  label_ids?: string[];
  archived?: boolean;
  /** Snooze until this ISO time, or null to bring it back now. */
  snoozed_until?: string | null;
  /** Set or change its Repeat, or null to stop it. */
  repeat?: TaskRepeat | null;
  /** Its type. */
  type_id?: string;
  size?: TaskSize | null;
  kind?: TaskKind | null;
  /** SF-T-151: refused on a mirrored task whose source maps priority (TASK_PRIORITY_UPSTREAM_OWNED). */
  priority?: TaskPriority | null;
  /** Keep Jev's suggestions for these fields as they are (T-17): clears their suggested mark. */
  confirm_suggested?: TaskSuggestedField[];
  prs?: TaskPr[];
  /** The whole new list; anyone left out stops being waited on. */
  waiting_on?: TaskWaitingRef[];
  /**
   * SF-T-174: the fleet machine the AI builder runs it on (one with a confirmed checkout of the project),
   * or null to let the pick-up choose. It stays until changed, so it can be switched between runs.
   */
  ai_machine_id?: string | null;
  /** T-42: a sub-task: show it on the Board too, or not. */
  show_on_board?: boolean;
  /**
   * SF-T-269: a sub-task: its one-at-a-time parent's chain stops here for a person once it has its PR (refused on a
   * task that isn't a sub-task). Handing the parent to the AI builder again (Continue) takes off the ones reached.
   */
  chain_break?: boolean;
  /** SF-T-242: build its sub-tasks one at a time (refused on a sub-task). */
  subtasks_one_at_a_time?: boolean;
  /** SF-T-242: its sub-tasks' runs (and its own) don't stop to ask (refused on a sub-task). */
  subtasks_defer_questions?: boolean;
  /**
   * SF-T-263: the sub-tasks picked for its one-at-a-time chain, replacing the last pick (a sub-task being built stays
   * in it); empty clears it, so the chain covers every open sub-task. Refused on a sub-task.
   */
  subtasks_chain?: string[];
  /**
   * SF-T-267: how many of its sub-tasks the one-at-a-time pick-up has with the AI builder at once, 1 to
   * SUBTASK_CONCURRENCY_MAX (1: one at a time). Refused on a sub-task.
   */
  subtasks_concurrency?: number;
  /**
   * SF-T-267: on a sub-task, the sub-tasks of the same parent it waits on (ids), replacing them; empty takes them off.
   * A one-at-a-time parent hands it to the AI builder once each has a PR or is done. A loop is refused, and so is
   * `after` with any other change (400 DEPENDENCIES_ALONE).
   */
  after?: string[];
  /**
   * SF-T-268: with `after`, the list it was changed from (what the editor showed). Then only the difference is written
   * (those added, those taken off) onto what it waits on by then, so two people changing it at once both keep theirs.
   */
  after_was?: string[];
  /** T-27: false promotes a draft (a person confirms it). Only the factory makes drafts; true is refused. */
  draft?: false;
}

/** PATCH /api/tasks/:id: the card, and the description's new version when the save changed it. */
export interface TaskUpdateResponse {
  task: TaskCard | null;
  description_updated_at?: string;
  /** T-59: the spec's new version when the save changed it. */
  spec_updated_at?: string;
}

export interface TaskMoveInput {
  column_id: string;
  /** Neighbours after the move (the card above and below); the server ranks it between them. */
  prev_task_id?: string | null;
  next_task_id?: string | null;
}

export interface TaskContextSearchHit extends TaskContextRef {
  title: string;
  subtitle: string | null;
}

/** A task in the top-bar search. */
export interface TaskSearchHit {
  id: string;
  number: number;
  title: string;
  column_name: string;
  is_done: boolean;
  archived: boolean;
  /** First linked thing, for a hint under the title. */
  context_title: string | null;
}

export interface TaskNotification {
  id: string;
  task_id: string;
  task_title: string;
  /** The task's number (T-42 is 42). */
  task_number: number;
  /**
   * `moved`: to Done. `review`: moved to Review. `unsnoozed`: its snooze ended and it's back on the
   * Board. `repeated`: its Repeat brought it back. `waiting`: it's your turn. `replied`: someone
   * it was waiting on commented. `unblocked`: a task it was waiting on is done (the excerpt names it).
   */
  kind: 'assigned' | 'mentioned' | 'chat' | 'moved' | 'review' | 'unsnoozed' | 'repeated' | 'waiting' | 'replied' | 'unblocked';
  actor_user_id: string | null;
  /** The person's name, or a non-person actor's label ("Factory · R-12"). */
  actor_name: string | null;
  /** Set when the actor's API key made the change. */
  via_api_key_name: string | null;
  excerpt: string | null;
  read: boolean;
  created_at: string;
}

/** The close code of a live socket refused because the user can't see the project: don't reconnect. */
export const TASK_LIVE_FORBIDDEN = 4403;

/** Messages the per-project live socket pushes to open pages. */

export type TaskLiveMessage =
  | { type: 'task'; task_id: string; actor_user_id: string | null }
  | { type: 'task_deleted'; task_id: string }
  | { type: 'board'; actor_user_id: string | null }
  | { type: 'notification' }
  /** T-37: the person marked Inbox items read or Done (sent to them only): their other pages' Inbox and count follow. */
  | { type: 'inbox' }
  /** GitHub said one of the task's pull requests changed (T-13): its card reads their live state again. */
  | { type: 'task_prs'; task_id: string }
  /**
   * Who has a task's description open to edit, across the project (T-46): sent to everyone when it
   * changes, and to a socket that says what it's editing (each one does when it opens).
   */
  | { type: 'editors'; editors: TaskEditor[] }
  /** SF-T-198: a Merge PRs batch moved on (sent to the person who started it): the dialog shows it. */
  | { type: 'pr_merge_batch'; batch: PrMergeBatch }
  /** Synced data or a record changed (`shared/types/live.ts`). */
  | LiveChangeMessage;

/**
 * SF-T-198: where one pull request in a Merge PRs batch is. `waiting`: GitHub is still working out whether
 * it merges cleanly (it does after each merge before it), looked at again shortly.
 */
export type PrMergeItemState = 'queued' | 'waiting' | 'merged' | 'skipped';

export interface PrMergeBatchItem {
  task_id: string;
  /** The task's number and title when the batch started, for the dialog. */
  number: number | null;
  title: string;
  url: string;
  state: PrMergeItemState;
  /** Why it was skipped (the merge's refusal: conflicts, checks, a draft…), or null. */
  reason: string | null;
}

/** SF-T-198: picked tasks' open pull requests, merged one at a time on the server, in the order picked. */
export interface PrMergeBatch {
  id: string;
  created_at: string;
  /** Null while it's still going. */
  finished_at: string | null;
  items: PrMergeBatchItem[];
}

/** The most pull requests one Merge PRs batch takes. */
export const PR_MERGE_BATCH_MAX = 50;

/** One tab with a task's description open to edit (T-46). */
export interface TaskEditor {
  user_id: string;
  /** The tab's own id, so a person's other tabs know it's theirs. */
  tab_id: string;
  task_id: string;
  /** How long since that tab last said it was editing (opened the editor, or typed), when sent. */
  idle_ms: number;
}

/**
 * What a page sends its live socket (T-46): the task whose description this tab is editing, or null
 * when it stopped. Sent on opening the editor, while typing (at most every TASK_EDITING_PING_MS) and
 * on every connect. A socket that closes stops editing.
 */
export interface TaskEditingClientMessage {
  type: 'editing';
  tab_id: string;
  task_id: string | null;
}

/** Typing tells the hub you're still editing at most this often. */
export const TASK_EDITING_PING_MS = 30_000;
/** Someone who hasn't typed for this long can be edited over ("Edit anyway"). */
export const TASK_EDITING_IDLE_MS = 5 * 60_000;

/** Days a task sits in a done Column before it leaves the Board. */
export const TASK_AUTO_ARCHIVE_DAYS = 14;

/** Longest snooze. */
export const TASK_SNOOZE_MAX_DAYS = 366;

export const TASK_TITLE_MAX = 300;
export const TASK_DESCRIPTION_MAX = 20000;
/** T-59: a spec can be as long as a description. */
export const TASK_SPEC_MAX = TASK_DESCRIPTION_MAX;
/** SF-T-185: a task comment; a feedback run's whole final message, its factory blocks included, fits. */
export const TASK_CHAT_MAX = 30000;
export const TASK_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** The columns every project starts with; flows move tasks by these names (case-insensitive). */
export const DEFAULT_TASK_COLUMNS: Array<Pick<TaskColumn, 'name' | 'is_done' | 'asks_ai'>> = [
  { name: 'Backlog', is_done: false, asks_ai: false },
  { name: 'Ready', is_done: false, asks_ai: true },
  { name: 'In progress', is_done: false, asks_ai: false },
  { name: 'Review', is_done: false, asks_ai: false },
  { name: 'Done', is_done: true, asks_ai: false },
];
